import type { RequestModel } from "../models/RequestModel";

import axios from "axios";
import { API_ENDPOINTS } from "../constants/const";
import log from "../logging/logger";

const CONTENT_TYPE_JSON = "application/json";
const SESSION_TOKEN_KEY = "sessionToken";

export const obtainSessionToken = (): string => {
    const existingToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (existingToken) {
        return existingToken;
    }
    const url = new URL(window.location.href);
    const token = url.searchParams.get("code") ?? "";
    if (token) {
        sessionStorage.setItem(SESSION_TOKEN_KEY, token);
        url.searchParams.delete("code");
        window.history.replaceState(
            {},
            document.title,
            url.pathname + url.search
        );
    }
    return token;
};

const getSessionToken = (): string => {
    return sessionStorage.getItem(SESSION_TOKEN_KEY) ?? "";
};

export const clearSessionToken = () => {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
};

const getApiHost = async (): Promise<string> => {
    //We don't need that, because of usage of relative path
    return "";
};

export const getEnvironmentConfig = async (): Promise<string> => {
    const apiUrl = await getApiHost();
    try {
        const response = await axios.get(apiUrl + API_ENDPOINTS.Config);
        return response.data.environment.toLowerCase();
    } catch (error) {
        log.error("Failed to get environment config", error);
        throw new Error("Failed to get environment config");
    }
};

export const getAppInsightConnectionString = async (): Promise<string> => {
    const apiUrl = await getApiHost();
    try {
        const response = await axios.get(apiUrl + API_ENDPOINTS.Config);
        return response.data.aiConnectionString;
    } catch (error) {
        log.error("Failed to get config", error);
        return "";
    }
}

export const getLogLevel = async (): Promise<string> => {
    const apiUrl = await getApiHost();
    try {
        const response = await axios.get(apiUrl + API_ENDPOINTS.Config);
        return response.data.FrontendLogLevel;
    } catch (error) {
        log.error("Failed to get config", error);
        return "";
    }
}

export const createRequest = async (data: RequestModel) => {
    const apiUrl = await getApiHost();
    const token = obtainSessionToken();

    try {
        const response = await axios.post(
            apiUrl + API_ENDPOINTS.CreateRequest,
            data,
            {
                headers: {
                    "Content-Type": CONTENT_TYPE_JSON,
                    "X-User-Token": token,
                    "X-User-Email": data.requesterEmail,
                },
            }
        );
        clearSessionToken();
        return response.data;
    } catch (error: unknown) {
        log.error("CreateRequest failed", error);
        if (axios.isAxiosError(error) && error.response?.status === 403) {
            throw new Error(
                "The Request link has expired due to lack of activity. To continue, please re-verify your email address. Click “Request” to start the process and receive a new link."
            );
        } else {
            throw new Error(
                "We encountered an issue while processing your request. Please try again later. We apologize for the inconvenience and appreciate your patience."
            );
        }
    }
};

export const sendVerificationEmail = async (email: string) => {
    const apiUrl = await getApiHost();

    const body = {
        email,
        languageCode: "en-us",
    };

    const response = await axios.post(
        apiUrl + API_ENDPOINTS.SendVerificationEmail,
        body,
        {
            headers: {
                "Content-Type": CONTENT_TYPE_JSON,
            },
        }
    );
    log.info("Email response:", response);

    if (response.status !== 200) {
        throw new Error("Failed to send verification email");
    }

    return response.data;
};

export const verifyToken = async (token: string) => {
    const apiUrl = await getApiHost();
    const effectiveToken = token || getSessionToken() || obtainSessionToken();
    if (!effectiveToken) {
        throw new Error("Token is required for verification");
    }
    const response = await axios.get(apiUrl + API_ENDPOINTS.TokenVerification, {
        params: { token: effectiveToken },
        headers: {
            "Content-Type": CONTENT_TYPE_JSON,
            "X-User-Token": effectiveToken,
        },
    });
    log.info("Token verification response:", response);

    if (response.status !== 200) {
        throw new Error("Failed to verify email token");
    }
    return response.data;
};

export const verifyEmailToken = async (
    token: string,
    requesterEmail: string
) => {
    const apiUrl = await getApiHost();
    const effectiveToken = token || getSessionToken() || obtainSessionToken();

    if (!effectiveToken) {
        throw new Error("Token is required for verification");
    }
    const response = await axios.get(
        apiUrl + API_ENDPOINTS.TokenEmailVerification,
        {
            params: { token: effectiveToken },
            headers: {
                "Content-Type": CONTENT_TYPE_JSON,
                "X-User-Token": effectiveToken,
                "X-User-Email": requesterEmail,
            },
        }
    );
    log.info("Verification response:", response);

    if (response.status !== 200) {
        throw new Error("Failed to verify email token");
    }
    return response.data;
};

export const uploadFile = async (
    fieldName: string,
    file: File,
    requesterEmail: string
): Promise<string> => {
    const apiUrl = await getApiHost();
    const token = getSessionToken() || obtainSessionToken();

    const formData = new FormData();
    formData.append(fieldName, file);
    const response = await axios.post(
        apiUrl + API_ENDPOINTS.UploadFile,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
                "X-User-Token": token,
                "X-User-Email": requesterEmail,
            },
        }
    );

    if (response.status !== 201) {
        throw new Error(`Failed to upload file for ${fieldName}`);
    }
    return response.data;
};

export interface SupportRequestData {
    brand: string;
    requesterName: string;
    requesterEmail: string;
    subject: string;
    message: string;
    currentPageUrl: string;
    languageCode?: string;
}

export interface SupportRequestResponse {
    supportReferenceId: string;
    status: string;
    submittedAt: string;
}

export const submitSupportRequest = async (
    data: SupportRequestData,
    attachments?: File[]
): Promise<SupportRequestResponse> => {
    const apiUrl = await getApiHost();

    const formData = new FormData();
    formData.append("brand", data.brand);
    formData.append("requesterName", data.requesterName);
    formData.append("requesterEmail", data.requesterEmail);
    formData.append("subject", data.subject);
    formData.append("message", data.message);
    formData.append("currentPageUrl", data.currentPageUrl);

    if (data.languageCode) {
        formData.append("languageCode", data.languageCode);
    }

    if (attachments) {
        for (const file of attachments) {
            formData.append("attachments", file);
        }
    }

    try {
        const response = await axios.post(
            apiUrl + API_ENDPOINTS.SupportRequest,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return response.data;
    } catch (error: unknown) {
        log.error("Support request submission failed", error);
        throw new Error(
            "We encountered an issue while submitting your support request. Please try again later."
        );
    }
};
